import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';

// reducer - state management
import { LOGOUT, SETQRIMAGE, REQUIRESETPASSWORD, SESSION_TIMEOUT } from 'store/actions';
import accountReducer from 'store/accountReducer';

// project imports
import Loader from 'ui-component/Loader';
import axios from 'utils/axios';
import {
    userLoginRequest,
    requestLoginOtp,
    verifyLoginOtp,
    verifySession,
    dashLogout,
    requestResetPassword,
    validateResetPasswordToken,
    changePassword,
    userValidateInitLogin,
    setPassword
} from 'api/auth';

import { LOGIN_TYPE } from 'constants';
import { IdleTimerProvider } from 'react-idle-timer';

// constant
const initialState = {
    isLoggedIn: false,
    isInitialized: false,
    user: null,
    initLogin: false,
    requireSetPassword: false,
    isSessionTimeout: false
};

const setSession = (serviceToken, loginType = LOGIN_TYPE['learner']) => {
    if (serviceToken) {
        localStorage.setItem('serviceToken', serviceToken);
        axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;

        // for login type
        localStorage.setItem('loginType', loginType);
    } else {
        localStorage.removeItem('serviceToken');
        delete axios.defaults.headers.common.Authorization;

        localStorage.removeItem('loginType');
    }
};

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
    const [state, dispatch] = useReducer(accountReducer, initialState);

    /**
     * Handle Session Timeout detection
     * To show re enter password modal
     */
    const handleSessionTimeout = (exp) => {
        const now = new Date();
        const timeoutMilliSeconds = exp * 1000 - now.getTime();

        if (timeoutMilliSeconds > 0) {
            // trigger the timeout
            setTimeout(() => {
                dispatch({
                    type: SESSION_TIMEOUT,
                    payload: {
                        isSessionTimeout: true
                    }
                });
            }, timeoutMilliSeconds);
        }
    };

    useEffect(() => {
        const init = async () => {
            try {
                const me = await verifySession();
                if (me?.success) {
                    dispatch({
                        type: 'LOGIN',
                        payload: {
                            isLoggedIn: true,
                            user: me?.data || {}
                        }
                    });

                    if (me?.data?.exp) {
                        handleSessionTimeout(me?.data?.exp);
                    }

                    dispatch({
                        type: REQUIRESETPASSWORD,
                        payload: {
                            requireSetPassword: me?.data?.requireSetPassword || false
                        }
                    });
                } else {
                    dispatch({
                        type: LOGOUT
                    });
                }
            } catch (err) {
                dispatch({
                    type: LOGOUT
                });
            }
        };

        init();
    }, []);

    const login = async ({ email, password, hasCaptcha = false, isRefresh = false }) => {
        const response = await userLoginRequest({ email, password, hasCaptcha, isRefresh });
        const { qrImage, id, tempSecret, exp } = response.data;

        // if is Refresh we will recall the handle session timeout
        if (isRefresh) {
            dispatch({
                type: 'SESSION_TIMEOUT',
                payload: {
                    isSessionTimeout: false
                }
            });
            if (exp) {
                handleSessionTimeout(exp);
            }
        } else {
            dispatch({
                type: SETQRIMAGE,
                payload: { qrImage, id, tempSecret }
            });
        }
    };

    // validate initial login token and email
    const validateInitLogin = async (email, token) => {
        const response = await userValidateInitLogin(email, token);
        const { id, tempSecret } = response.data;
        dispatch({
            type: SETQRIMAGE,
            payload: { id, tempSecret, initLogin: true }
        });
    };

    const logout = async () => {
        setSession(null);
        await dashLogout();
        dispatch({ type: LOGOUT });
    };

    const resetPassword = async (email, hasCaptcha = false) => {
        try {
            const response = await requestResetPassword(email, hasCaptcha);
            return response;
        } catch (e) {
            throw new Error(e.message);
        }
    };

    const changePasswordViaToken = async ({ token, password }) => {
        try {
            return await changePassword(token, password);
        } catch (e) {
            throw new Error(e.message);
        }
    };

    const requestOtpViaEmail = async (type = 'init') => {
        try {
            return await requestLoginOtp(type);
        } catch (e) {
            throw new Error(e.message);
        }
    };

    const setUserPassword = async (password) => {
        try {
            const data = await setPassword(password);
            dispatch({
                type: REQUIRESETPASSWORD,
                payload: {
                    requireSetPassword: false
                }
            });
            return data;
        } catch (e) {
            throw new Error(e.message);
        }
    };

    const verifyResetPasswordToken = async (token) => {
        try {
            const response = await validateResetPasswordToken(token);
            return response.data;
        } catch (e) {
            throw new Error(e.message);
        }
    };

    const verifyOtp = async (otp, method, hasCaptcha) => {
        try {
            const response = await verifyLoginOtp(otp, method, hasCaptcha);
            const { data } = response;

            const sessionResponse = await verifySession();
            const { data: user } = sessionResponse;
            dispatch({
                type: 'LOGIN',
                payload: {
                    isLoggedIn: true,
                    user
                }
            });

            dispatch({
                type: REQUIRESETPASSWORD,
                payload: {
                    requireSetPassword: data?.requireSetPassword || false
                }
            });

            if (data?.exp) {
                handleSessionTimeout(data?.exp);
            }

            return response;
        } catch (e) {
            throw new Error(e.message);
        }
    };
    if (state.isInitialized !== undefined && !state.isInitialized) {
        return <Loader />;
    }

    // Inactivity auto logout
    const handleOnIdle = async () => {
        logout();
    };

    return (
        <IdleTimerProvider
            timeout={1000 * 60 * (process.env.REACT_APP_INACTIVITY_TIMEOUT || 45)}
            onIdle={handleOnIdle}
            crossTab={true}
            name="MY_APP_IDLE_TIMER"
        >
            <JWTContext.Provider
                // eslint-disable-next-line prettier/prettier
                    value={{
                    ...state,
                    login,
                    logout,
                    resetPassword,
                    requestOtpViaEmail,
                    verifyOtp,
                    verifyResetPasswordToken,
                    changePasswordViaToken,
                    validateInitLogin,
                    setUserPassword
                }}
            >
                {children}
            </JWTContext.Provider>
        </IdleTimerProvider>
    );
};

JWTProvider.propTypes = {
    children: PropTypes.node
};

export default JWTContext;
